import React, { useEffect } from "react";
import {
    AutocompleteDropdown,
    ClearFiltersButton,
    ContentSection,
    DatePicker,
    DetailsRowProps,
    DropdownMultiselect,
    ensure,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    MultiselectSearchDropdown,
    RenderFunction,
    SearchBox,
    Stack,
    Theme
} from "sprint-common-web";

import { HeaderBreadcrumb } from "../../../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { PageHeader } from "../../../common/ui/layout/header/PageHeader";
import { ordersRoutes } from "../../OrdersRoutes";
import { createOnPremOrderListingButtons } from "./createOnPremOrderListingButtons";
import { createOnPremOrderListingColumns } from "./createOnPremOrderListingColumns";
import { useOnPremOrderListing } from "./hooks/useOnPremOrderListing";
import { useOnPremOrderListingFilters } from "./hooks/useOnPremOrderListingFilters";
import { OnPremOrderListingItem } from "./OnPremOrderListingItem";

export const OnPremOrderListing: React.VFC = () => {
    const {
        filters,
        statusOptions,
        sourceOptions,
        airlineOptions,
        areAirlinesLoading,
        systemOptions,
        progressOptions,
        filterDistributorOptions,
        setSort,
        setPage,
        setPageSize,
        setStatuses,
        setSources,
        clearFilters,
        setContent,
        setPONumber,
        setDistributor,
        setAirlines,
        setSystems,
        setFilename,
        setProgresses,
        setDueDateFrom,
        setDueDateTo,
        setStartDateFrom,
        setStartDateTo
    } = useOnPremOrderListingFilters();

    const { items, totalItems, isLoading, getData, selectedItems, setSelectedItems } = useOnPremOrderListing();

    useEffect(() => {
        getData(filters);
    }, [filters]);

    const columns = createOnPremOrderListingColumns();

    const buttons = createOnPremOrderListingButtons({ filters, setPageSize });

    return (
        <>
            <PageHeader>
                <HeaderBreadcrumb routes={ordersRoutes} />
            </PageHeader>
            <ContentSection header="On-Prem Orders" buttons={buttons}>
                <Grid>
                    <GridRow>
                        <GridColumn sm={2}>
                            <SearchBox
                                testId="on-prem-orders-filters-poNumber"
                                placeholder="PO Number"
                                onChange={setPONumber}
                                value={filters.pONumber}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <SearchBox
                                testId="on-prem-orders-filters-content"
                                placeholder="Content"
                                onChange={setContent}
                                value={filters.content}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <SearchBox
                                testId="on-prem-orders-filters-filename"
                                placeholder="Filename"
                                onChange={setFilename}
                                value={filters.filename}
                            />
                        </GridColumn>
                        <GridColumn sm={3}>
                            <DropdownMultiselect
                                testId="on-prem-orders-filters-status"
                                placeholder="Status"
                                selectedKeys={filters.statuses}
                                onChange={setStatuses}
                                options={statusOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={1}>
                            <DropdownMultiselect
                                testId="on-prem-orders-filters-sources"
                                placeholder="Source"
                                selectedKeys={filters.sources}
                                onChange={setSources}
                                options={sourceOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <Stack horizontalAlign="end">
                                <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                            </Stack>
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="on-prem-orders-filters-start-date-from"
                                placeholder="Start date from"
                                value={filters.startDateFrom}
                                onDateSelect={setStartDateFrom}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="on-prem-orders-filters-start-date-to"
                                placeholder="Start date to"
                                value={filters.startDateTo}
                                onDateSelect={setStartDateTo}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <DatePicker
                                testId="on-prem-orders-filters-due-date-from"
                                placeholder="Due date from"
                                value={filters.dueDateFrom}
                                onDateSelect={setDueDateFrom}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <DatePicker
                                testId="on-prem-orders-filters-due-date-to"
                                placeholder="Due date to"
                                value={filters.dueDateTo}
                                onDateSelect={setDueDateTo}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow style={{ paddingTop: 10 }}>
                        <GridColumn sm={2}>
                            <DropdownMultiselect
                                testId="orders-filters-progress"
                                placeholder="Progress"
                                selectedKeys={filters.progresses}
                                onChange={setProgresses}
                                options={progressOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={2}>
                            <AutocompleteDropdown
                                testId="on-prem-orders-filters-distributor"
                                placeholder="Distributor"
                                selectedKey={filters.distributorId}
                                selectedText={filters.distributorName}
                                onChange={setDistributor}
                                onSearch={filterDistributorOptions}
                            />
                        </GridColumn>
                        <GridColumn sm={3}>
                            <MultiselectSearchDropdown
                                options={airlineOptions}
                                placeholder="Airlines"
                                onChange={setAirlines}
                                selectedKeys={filters.airlineIds}
                                disabled={!airlineOptions.length}
                            />
                        </GridColumn>

                        <GridColumn sm={2}>
                            <MultiselectSearchDropdown
                                options={systemOptions}
                                placeholder="Systems"
                                onChange={setSystems}
                                selectedKeys={filters.systemIds}
                                disabled={!filters.airlineIds?.length}
                            />
                        </GridColumn>
                    </GridRow>
                    <GridRow>
                        <GridColumn sm={12}>
                            <ListTable
                                columns={columns}
                                selectedItems={selectedItems}
                                items={items}
                                isLoading={isLoading || areAirlinesLoading}
                                pagination={{
                                    page: filters.page,
                                    pageSize: filters.pageSize,
                                    totalItems,
                                    onPageChange: setPage,
                                    onPageSizeChange: setPageSize
                                }}
                                sort={{ by: filters.sortBy, order: filters.sortOrder }}
                                onSelectionChange={setSelectedItems}
                                onRenderRow={renderRow}
                                onSortChange={setSort}
                                layoutMode="fixedColumns"
                                isStickyModeEnabled={true}
                            />
                        </GridColumn>
                    </GridRow>
                </Grid>
            </ContentSection>
        </>
    );
};

const renderRow = (rowProps?: DetailsRowProps, defaultRender?: RenderFunction<DetailsRowProps>) => {
    ensure(rowProps);
    ensure(defaultRender);

    const item = rowProps.item as OnPremOrderListingItem;
    const testProps = {
        "data-testid": "table-row",
        "data-testkey": rowProps.item.id
    };

    if (item.isModified) {
        return renderCustomRow(rowProps, defaultRender, RowType.Modified);
    }

    if (item.isPinned) {
        return renderCustomRow(rowProps, defaultRender, RowType.Pinned);
    }

    return defaultRender({ ...testProps, ...rowProps }, defaultRender);
};

enum RowType {
    Modified = 1,
    Pinned = 3
}

const colorsByRowType = {
    [RowType.Modified]: Theme.palette.blue,
    [RowType.Pinned]: Theme.palette.green
};

const renderCustomRow = (
    rowProps: DetailsRowProps,
    defaultRender: RenderFunction<DetailsRowProps>,
    type: RowType
): JSX.Element | null => {
    return defaultRender({
        ...rowProps,
        styles: {
            root: {
                userSelect: "auto",
                borderLeft: `4px solid ${colorsByRowType[type]}`
            }
        }
    });
};
